import React, { useState } from 'react';
import { Button, Chip, IconButton } from '@mui/material';
import {
    LocationOn,
    CalendarMonth,
    WatchLater,
    HourglassBottom,
    MoreVert,
    Share,
    Flag,
    Favorite,
    FavoriteBorder
} from '@mui/icons-material';
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import EventSharePopup from '../EventMetaDescription/EventSharePopup';
import SnackBar from '../FormFields/Snackbar';
import Menus from '../Dialog/Menus';
import { formatEventDate } from '../../utils/dateFormatter';
import { H3, Body2, Body3 } from '../../themes/typography';
import { ShmoozShadowLogo } from '../../assets/images';
dayjs.extend(durationPlugin);

const eventImage =
    'https://images.unsplash.com/photo-1728357728879-ac9a5c703c2c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxmZWF0dXJlZC1waG90b3MtZmVlZHw5fHx8ZW58MHx8fHx8';

// fcs => format category sub-category
export default function BasicDetailSection({
    fcs = [],
    endDate = dayjs().format('YYYY-MM-DD'),
    endTime,
    startTime,
    startDate = dayjs().format('YYYY-MM-DD'),
    eventType,
    schedules,
    minTicketPrice,
    showTicketPreview,
    showElipsis = true,
    thumbnailUrl = eventImage,
    name = 'Summer Music Festival Concert Preview',
    location = '52 San Adres New Your City, London, USA',
    setShowTicketPreview = () => {},
    scrollToEventSchedule = () => {}
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const [openEventSharePopup, setOpenEventSharePopup] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [isInterested, setIsInterested] = useState(false);

    function handleClose() {
        setAnchorEl(null);
    }

    function handleClick(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleOpenEventSharePopup() {
        setOpenEventSharePopup(true);
    }

    function handleCloseEventSharePopup() {
        setOpenEventSharePopup(false);
    }

    function handleInterestClick() {
        setIsInterested(!isInterested);
        setOpenSnackbar(true);
    }

    const snackbarMessage = isInterested
        ? 'Thank you for showing interest in this event!'
        : 'You have removed your interest in this event.';

    const menusList = [
        {
            id: 'interest',
            icon: isInterested ? (
                <Favorite style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />
            ) : (
                <FavoriteBorder style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />
            ),
            menuName: isInterested ? 'Interested' : 'Interest',
            onClick: handleInterestClick
        },
        {
            id: 'share',
            icon: <Share style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Share',
            onClick: handleOpenEventSharePopup
        },
        {
            id: 'report',
            icon: <Flag style={{ color: 'var(--tertiary-color)', fontSize: '1.5rem' }} />,
            menuName: 'Report an issue',
            link: '/report-event'
        }
    ];

    function getEventDate() {
        if (eventType === 'single_day') {
            return formatEventDate(startDate);
        } else if (eventType === 'multi_day') {
            return `${formatEventDate(startDate)} - ${formatEventDate(endDate)}`;
        } else {
            return `${formatEventDate(startDate)} to ${formatEventDate(endDate)}`;
        }
    }

    function getEventTiming() {
        if (eventType === 'single_day' || eventType === 'continuous') {
            return `${startTime} - ${endTime}`;
        } else {
            return (
                <div style={{ display: 'flex', gap: '5px' }}>
                    {!showTicketPreview ? (
                        <>
                            <Body2>Multiple Days</Body2>
                            <Body2
                                color="var(--tertiary-color)"
                                style={{ cursor: 'pointer' }}
                                onClick={scrollToEventSchedule}
                            >
                                [View Event Schedule]
                            </Body2>
                        </>
                    ) : (
                        <Body2>Multiple Days</Body2>
                    )}
                </div>
            );
        }
    }

    // function calculateDuration() {
    //     if (!startDate || !startTime || !endDate || !endTime) return null;

    //     const startDateTime = dayjs(`${startDate} ${startTime}`);
    //     const endDateTime = dayjs(`${endDate} ${endTime}`);
    //     const diff = dayjs.duration(endDateTime.diff(startDateTime));

    //     const hours = diff.hours();
    //     const minutes = diff.minutes();

    //     if (hours && minutes) {
    //         return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    //     } else if (hours) {
    //         return `${hours} hour${hours > 1 ? 's' : ''}`;
    //     } else if (minutes) {
    //         return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    //     } else {
    //         return 'Less than a minute';
    //     }
    // }

    function calculateDuration() {
        if (eventType === 'continuous') {
            if (!schedules || schedules.length === 0) return 'Duration not available';

            let totalDuration = 0;

            schedules?.forEach((schedule) => {
                const fromTime = dayjs(`${schedule.date} ${schedule.from_time}`);
                const toTime = dayjs(`${schedule.date} ${schedule.to_time}`);
                const diff = dayjs.duration(toTime.diff(fromTime));
                totalDuration += diff.asMinutes(); // Accumulate total duration in minutes
            });

            const hours = Math.floor(totalDuration / 60);
            const minutes = totalDuration % 60;

            if (hours && minutes) {
                return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else if (hours) {
                return `${hours} hour${hours > 1 ? 's' : ''}`;
            } else if (minutes) {
                return `${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else {
                return 'Less than a minute';
            }
        } else if (eventType === 'single_day') {
            if (!startDate || !startTime || !endDate || !endTime) return null;

            const firstSchedule = schedules?.[0];
            if (firstSchedule?.slots && firstSchedule.slots.length > 0) {
                return (
                    <React.Fragment>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            {!showTicketPreview ? (
                                <>
                                    <Body2>Multiple Slots</Body2>
                                    <Body2
                                        color="#26C1E8"
                                        style={{ cursor: 'pointer' }}
                                        onClick={scrollToEventSchedule}
                                    >
                                        [View Event Schedule]
                                    </Body2>
                                </>
                            ) : (
                                <Body2>Multiple Slots</Body2>
                            )}
                        </div>
                    </React.Fragment>
                );
            }

            const startDateTime = dayjs(`${startDate} ${startTime}`);
            const endDateTime = dayjs(`${endDate} ${endTime}`);
            const diff = dayjs.duration(endDateTime.diff(startDateTime));

            const hours = diff.hours();
            const minutes = diff.minutes();

            if (hours && minutes) {
                return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else if (hours) {
                return `${hours} hour${hours > 1 ? 's' : ''}`;
            } else if (minutes) {
                return `${minutes} minute${minutes > 1 ? 's' : ''}`;
            } else {
                return 'Less than a minute';
            }
        } else {
            return null;
        }
    }

    const eventDuration = calculateDuration();

    return (
        <React.Fragment>
            <div className="preview-basic-detail-container">
                <div className="preview-basic-detail-banner">
                    <div className="event-preview-thumbnail-image">
                        <img src={thumbnailUrl} alt="Event" />
                    </div>

                    <div className="preview-basic-detail">
                        <div className="preview-event-title">
                            <H3 color="var(--secondary-color)" fontWeight={700}>
                                {name}
                            </H3>
                        </div>

                        <div className="event-preview-schedule-details">
                            <div className="event-preview-main-schedule-container">
                                <div className="event-preview-main-schedule-left">
                                    <div className="preview-location-format-price-details">
                                        <div className="preview-event-location">
                                            <LocationOn sx={iconStyle} />
                                            <Body2>{location}</Body2>
                                        </div>

                                        <div className="preview-event-tags">
                                            {fcs?.map((tag, index) => (
                                                <CustomChip key={index} name={tag?.name} />
                                            ))}
                                        </div>

                                        {minTicketPrice && (
                                            <div className="preview-event-starting-price">
                                                {minTicketPrice > 0 ? (
                                                    <Body2>
                                                        From{' '}
                                                        <span
                                                            style={{
                                                                color: 'var(--secondary-color)'
                                                            }}
                                                        >
                                                            ₹{minTicketPrice}
                                                        </span>{' '}
                                                        Onwards
                                                    </Body2>
                                                ) : (
                                                    <Body2>Free Entry</Body2>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Event Right side Details */}
                                <div className="event-meta-right-side-section">
                                    {/* Event Date */}
                                    <div className="event-preview-date-detail">
                                        <CalendarMonth sx={iconStyleWithoutMargin} />
                                        <Body2>{getEventDate()}</Body2>
                                    </div>

                                    {/* Condtionally render Event Time if prop is provided when reuse this event banner */}
                                    {startTime && endTime && (
                                        <div className="event-preview-date-detail">
                                            <WatchLater sx={iconStyleWithoutMargin} />
                                            <Body2>{getEventTiming()}</Body2>
                                        </div>
                                    )}

                                    {/* Will have to count duration based on startTime and endTime of the event. startDate and endDate is required when we calculate duration. So there will be 2 parameters should be pass in function. 1. startTime and endTime. 2. startDate and endDate */}
                                    {eventDuration && (
                                        <div className="event-preview-date-detail">
                                            <HourglassBottom sx={iconStyleWithoutMargin} />
                                            <Body2>{eventDuration}</Body2>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="preview-event-get-tickets-button">
                                <div>
                                    <Button
                                        variant="main"
                                        onClick={() => setShowTicketPreview(!showTicketPreview)}
                                    >
                                        {showTicketPreview ? 'View Event' : 'Get Ticket'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="preview-event-shmooz-shadow">
                        {showElipsis && (
                            <div className="elipsis-menu">
                                <IconButton
                                    color="elipsis"
                                    onClick={handleClick}
                                    aria-controls={open ? 'event-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                >
                                    <MoreVert sx={{ fontSize: '2rem' }} />
                                </IconButton>
                                <Menus
                                    id="ticket-menus"
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClick={handleClose}
                                    onClose={handleClose}
                                    menus={menusList}
                                    shouldDisabledMenus={true}
                                />
                            </div>
                        )}

                        <div className="preview-event-shmooz-shadow-logo">
                            <img src={ShmoozShadowLogo} alt="Shmooz" />
                        </div>
                    </div>
                </div>
            </div>

            <SnackBar
                openSnack={openSnackbar}
                setOpenSnack={setOpenSnackbar}
                message={snackbarMessage}
            />
            <EventSharePopup
                open={openEventSharePopup}
                onClose={handleCloseEventSharePopup}
                shareEventName={name}
            />
        </React.Fragment>
    );
}

function CustomChip({ name = 'Travel' }) {
    return (
        <Chip
            label={
                <Body3 fontWeight={500} color="var(--tertiary-color)">
                    {name}
                </Body3>
            }
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                border: '1px solid var(--quaternary-color)',
                padding: '0px 14px',
                minHeight: '24px',
                maxHeight: '44px',
                borderRadius: '1.5rem',
                boxSizing: 'border-box',
                textTransform: 'capitalize',
                backgroundColor: 'transparent',
                '& .MuiChip-label': {
                    padding: '0px'
                }
            }}
        />
    );
}

const iconStyle = {
    color: 'var(--tertiary-color)',
    fontSize: '1.25rem',
    marginTop: '2px'
};

const iconStyleWithoutMargin = {
    color: 'var(--tertiary-color)',
    fontSize: '1.25rem',
    marginTop: '0px'
};